import { useMemo } from 'react'

/*
 * CareConstellation — the home-page hero visual.
 *
 * A single diagram that communicates the whole product in one glance:
 *
 *   - Person at the centre (the visitor)
 *   - Five care-system nodes orbiting around them, in the same order as
 *     the CareFlow strip (Consultation → Expert Psychiatrist →
 *     Assessment → Therapy + Medicine → Ongoing care)
 *   - Numbered badges on the nodes imply a forward path, while the
 *     closed loop itself communicates continuous, coordinated care
 *   - Faint concentric rings + a halo behind the person suggest the
 *     care system gently holding the visitor
 *
 * The visual is pure SVG so it stays crisp, lightweight, and
 * animatable. All motion is wrapped in `.cc-*` classes that respect
 * `prefers-reduced-motion` (see AumhumSite.css).
 */

// Layout — tuned so labels never clip inside the viewBox.
const VIEW_W = 580
const VIEW_H = 520
const CENTER_X = 290
const CENTER_Y = 260
const ORBIT_R = 172
const NODE_R = 34
// Start at -90° (12 o'clock) and sweep clockwise.
const START_ANGLE_DEG = -90

const NODES = [
  {
    id: 'consultation',
    label: 'Consultation',
    sub: 'Care coordinator',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M3 5a2 2 0 0 1 2-2h2.28a1 1 0 0 1 .95.68l1 2.93a1 1 0 0 1-.25 1L7.91 9.09a11 11 0 0 0 6 6l1.48-1.07a1 1 0 0 1 1-.25l2.93 1a1 1 0 0 1 .68.95V18a2 2 0 0 1-2 2A17 17 0 0 1 3 5z" />
      </svg>
    ),
  },
  {
    id: 'psychiatrist',
    label: 'Psychiatrist',
    sub: 'Leading authority',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M6 3v6a4 4 0 0 0 8 0V3" />
        <path d="M14 9a5 5 0 0 0 5 5 5 5 0 0 0 0-10" />
        <circle cx="19" cy="4" r="1.2" fill="currentColor" stroke="none" />
        <path d="M10 13v2a5 5 0 0 0 10 0" />
      </svg>
    ),
  },
  {
    id: 'assessment',
    label: 'Assessment',
    sub: 'Clinical psychology',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4V3a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" />
        <line x1="9" y1="10" x2="15" y2="10" />
        <line x1="9" y1="14" x2="15" y2="14" />
        <line x1="9" y1="18" x2="13" y2="18" />
      </svg>
    ),
  },
  {
    id: 'therapy-medicine',
    label: 'Therapy + Medicine',
    sub: 'Senior clinicians',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    id: 'ongoing',
    label: 'Ongoing care',
    sub: 'Same team, always',
    Icon: (p) => (
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...p}>
        <polyline points="17 1 21 5 17 9" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
  },
]

function polar(angleDeg) {
  const r = (angleDeg * Math.PI) / 180
  return [CENTER_X + ORBIT_R * Math.cos(r), CENTER_Y + ORBIT_R * Math.sin(r)]
}

export default function CareConstellation() {
  const nodes = useMemo(() => {
    const step = 360 / NODES.length
    return NODES.map((n, i) => {
      const angle = START_ANGLE_DEG + i * step
      const [x, y] = polar(angle)
      // Labels: push them a little further out so they sit outside
      // the node circle rather than overlapping the icon.
      const labelRadial = ORBIT_R + NODE_R + 18
      const lr = (angle * Math.PI) / 180
      const lx = CENTER_X + labelRadial * Math.cos(lr)
      const ly = CENTER_Y + labelRadial * Math.sin(lr)
      return { ...n, cx: x, cy: y, lx, ly, angle, stepNumber: i + 1 }
    })
  }, [])

  return (
    <svg
      className="cc-svg"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      role="img"
      aria-label="A person at the centre of AUMHUM's connected care system: Consultation, Expert Psychiatrist, Assessment, Therapy with medicine, and Ongoing care."
      width="100%"
    >
      <defs>
        <radialGradient id="cc-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#c4b5fd" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#a78bfa" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="cc-person-bg" cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f5f3ff" />
        </radialGradient>
        <linearGradient id="cc-link" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id="cc-ring" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e0e7ff" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#ede9fe" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* Background concentric rings — give the diagram depth without
          stealing attention from the nodes. */}
      <g className="cc-bg-rings" aria-hidden="true">
        <circle cx={CENTER_X} cy={CENTER_Y} r={ORBIT_R + 54} fill="none"
          stroke="url(#cc-ring)" strokeWidth="1" strokeDasharray="2 6" />
        <circle cx={CENTER_X} cy={CENTER_Y} r={ORBIT_R + 22} fill="none"
          stroke="#ede9fe" strokeWidth="1" strokeDasharray="2 6" />
        <circle cx={CENTER_X} cy={CENTER_Y} r={ORBIT_R - 30} fill="none"
          stroke="#f5f3ff" strokeWidth="1" />
      </g>

      {/* Halo behind the person — a soft glow that "breathes". */}
      <circle className="cc-halo" cx={CENTER_X} cy={CENTER_Y} r="118"
        fill="url(#cc-halo)" />

      {/* Connection lines from person to each node. Drawn before the
          nodes so the nodes sit on top visually. */}
      <g aria-hidden="true" stroke="url(#cc-link)" strokeWidth="1.4"
        strokeLinecap="round">
        {nodes.map((n) => (
          <line
            key={`link-${n.id}`}
            x1={CENTER_X}
            y1={CENTER_Y}
            x2={n.cx}
            y2={n.cy}
            strokeDasharray="3 5"
          />
        ))}
      </g>

      {/* Forward-motion arc: a subtle dashed arc along the top-right
          quadrant with an arrowhead, hinting at the "path forward".
          Placed after the cross-lines so it reads louder. */}
      <g className="cc-forward" aria-hidden="true">
        <defs>
          <marker
            id="cc-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#7c3aed" />
          </marker>
        </defs>
        <path
          d={`M ${CENTER_X + ORBIT_R - 8} ${CENTER_Y - 30}
              A ${ORBIT_R - 10} ${ORBIT_R - 10} 0 0 0
              ${CENTER_X + ORBIT_R - 60} ${CENTER_Y - 90}`}
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeDasharray="4 5"
          markerEnd="url(#cc-arrow)"
          opacity="0.85"
        />
      </g>

      {/* Care nodes */}
      {nodes.map((n, i) => {
        const labelAnchor =
          Math.cos((n.angle * Math.PI) / 180) > 0.2
            ? 'start'
            : Math.cos((n.angle * Math.PI) / 180) < -0.2
              ? 'end'
              : 'middle'
        // Label y offset so text sits slightly below horizontal nodes
        // and centred for top/bottom ones.
        return (
          <g key={n.id} className="cc-node" style={{ '--cc-delay': `${i * 80}ms` }}>
            {/* shadow/base */}
            <circle
              cx={n.cx}
              cy={n.cy + 3}
              r={NODE_R}
              fill="#0f172a"
              opacity="0.08"
            />
            {/* node body */}
            <circle
              cx={n.cx}
              cy={n.cy}
              r={NODE_R}
              fill="#ffffff"
              stroke="#e9d5ff"
              strokeWidth="1.25"
            />
            {/* step badge */}
            <g>
              <circle
                cx={n.cx + NODE_R - 8}
                cy={n.cy - NODE_R + 6}
                r="10"
                fill="#7c3aed"
              />
              <text
                x={n.cx + NODE_R - 8}
                y={n.cy - NODE_R + 10}
                textAnchor="middle"
                fontSize="10.5"
                fontWeight="700"
                fill="#ffffff"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {n.stepNumber}
              </text>
            </g>
            {/* icon centred in node */}
            <g
              transform={`translate(${n.cx - 10}, ${n.cy - 10})`}
              style={{ color: '#6d28d9' }}
            >
              <n.Icon />
            </g>
            {/* label + subtitle */}
            <text
              x={n.lx}
              y={n.ly}
              textAnchor={labelAnchor}
              fontSize="13"
              fontWeight="600"
              fill="#0f172a"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {n.label}
            </text>
            <text
              x={n.lx}
              y={n.ly + 15}
              textAnchor={labelAnchor}
              fontSize="11"
              fill="#64748b"
              fontFamily="ui-sans-serif, system-ui, sans-serif"
            >
              {n.sub}
            </text>
          </g>
        )
      })}

      {/* Centre: the person. Soft white disc with a silhouette, a
          small "You" tag, and a gently breathing halo (set above). */}
      <g className="cc-person">
        <circle
          cx={CENTER_X}
          cy={CENTER_Y}
          r="78"
          fill="url(#cc-person-bg)"
          stroke="#ddd6fe"
          strokeWidth="1.25"
        />
        <g transform={`translate(${CENTER_X}, ${CENTER_Y})`}>
          {/* head */}
          <circle cx="0" cy="-14" r="16" fill="#c4b5fd" />
          {/* shoulders */}
          <path
            d="M -30 22 Q 0 0 30 22 L 30 44 L -30 44 Z"
            fill="#a78bfa"
          />
          {/* cover the bottom edge inside the circle so the silhouette
              reads like head + shoulders, not a full torso. */}
          <rect x="-40" y="36" width="80" height="30" fill="url(#cc-person-bg)" />
        </g>
        {/* "You" chip */}
        <g>
          <rect
            x={CENTER_X - 22}
            y={CENTER_Y + 42}
            width="44"
            height="20"
            rx="10"
            fill="#1e293b"
          />
          <text
            x={CENTER_X}
            y={CENTER_Y + 56}
            textAnchor="middle"
            fontSize="11"
            fontWeight="700"
            letterSpacing="0.06em"
            fill="#ffffff"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
          >
            YOU
          </text>
        </g>
      </g>
    </svg>
  )
}
